import { Icon } from '@iconify/react'
import { useNotification } from 'app/providers'
import classNames from 'classnames'
import { Button } from 'common/components/Button/Button'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { BUTTON_PROPORTION, BUTTON_SEVERITY } from 'common/constants/buttonConstants'
import { useAppSelector } from 'common/hooks/redux'
import { DateService } from 'common/services/dateService'
import { useProcedureActions } from 'features/Procedure/hooks/useProcedureActions'
import type { ProcedureItem } from 'features/Procedure/state/procedure-api.type'
import {
  useFetchProceduresQuery,
  useUpdateProcedureMutation,
} from 'features/Procedure/state/procedure.api'
import { ProcedureTypeCode } from 'features/Procedure/state/procedure.type'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { useToggle } from 'react-use'
import { baseColumns, getTreatmentsTableColumns } from './columns'
import { ManageProcedure } from './components/manage-procedure/manage-procedure'
import { ProductScanner } from './components/product-scanner'
import { GeneralNote } from './components/general-note'
import { TreatmentsDataSection } from './components/treatments-data-section'
import { TreatmentsHistoricalSection } from './components/treatments-historical-section'
import { PreviousTreatments, toPreviousTreatments } from './previous-treatments'
import styles from './treatments.module.scss'
import { Spin } from 'antd'
import TextArea from 'antd/es/input/TextArea'

const NOTES_AUTOSAVE_DELAY_MS = 2000
type GeneralNoteAutosaveStatus = 'idle' | 'saving' | 'saved' | 'error'

const toProcedurePayloadBase = (procedure: ProcedureItem) => ({
  id: procedure.id,
  code: procedure.code,
  code_type: procedure.code_type,
  procedure_units: procedure.procedure_units,
  units: procedure.units,
  fee: procedure.fee,
  provider_id: procedure.provider_id,
  justify_codes: procedure.justify_codes,
})

type ProcedureMutationFailureResponse = {
  message?: string
  error?: string
  success?: boolean
  status?: boolean
}

const getMutationFailureMessage = (response: unknown, fallbackMessage: string): string | null => {
  if (!response || typeof response !== 'object') {
    return null
  }

  const mutationResponse = response as ProcedureMutationFailureResponse
  const isSuccessFalse =
    typeof mutationResponse.success === 'boolean' && mutationResponse.success === false
  const isStatusFalse =
    typeof mutationResponse.status === 'boolean' && mutationResponse.status === false

  if (!isSuccessFalse && !isStatusFalse) {
    return null
  }

  return mutationResponse.error || mutationResponse.message || fallbackMessage
}

export type TreatmentsProps = {
  onRegisterAction?: (action: ReactNode | null) => void
  selectedPatientId: number | null
  selectedEncounterUid: string | null
  disableActions?: boolean
  onNavigateToForms?: (documentId?: number | null) => void
  onRegisterLeaveAction?: ((handler: (() => Promise<void>) | null) => void) | undefined
}

export const Treatments = ({
  onRegisterAction,
  selectedPatientId,
  selectedEncounterUid,
  disableActions = false,
  onNavigateToForms,
  onRegisterLeaveAction,
}: TreatmentsProps) => {
  const [isVisibleManageProcedure, toggleManageProcedure] = useToggle(false)
  const [selectedManageProcedure, setSelectedManageProcedure] = useState<ProcedureItem | null>(null)
  const [isVisibleProductScanner, toggleProductScanner] = useToggle(false)
  const [procedureToScan, setProcedureToScan] = useState<ProcedureItem | null>(null)
  const selectedProcedureId = useAppSelector(
    (state) => state.procedureReducer.selectedProcedure?.id,
  )
  const { siteId } = useParams()
  const { handleSetSelectedProcedure } = useProcedureActions()
  const { setNotification } = useNotification()
  const [updateProcedure, { isLoading: isUpdatingProcedure }] = useUpdateProcedureMutation()
  const [autosaveUpdateProcedure] = useUpdateProcedureMutation()
  const [noteDrafts, setNoteDrafts] = useState<Record<number, string>>({})
  const [generalNoteDraft, setGeneralNoteDraft] = useState<string>('')
  const [isEditingGeneralNote, setIsEditingGeneralNote] = useState(false)
  const [generalNoteAutosaveStatus, setGeneralNoteAutosaveStatus] =
    useState<GeneralNoteAutosaveStatus>('idle')
  const [generalNoteLastSavedAt, setGeneralNoteLastSavedAt] = useState<string | null>(null)
  const activeTranscriptionRef = useRef<{
    procedureId: number
    baseValue: string
  } | null>(null)
  const procedureNoteAutosaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const generalNoteAutosaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isProcedureNoteAutosavingRef = useRef(false)
  const rerunProcedureNoteAutosaveRef = useRef(false)
  const isGeneralNoteAutosavingRef = useRef(false)
  const rerunGeneralNoteAutosaveRef = useRef(false)

  const { data: proceduresData, isFetching } = useFetchProceduresQuery(
    {
      siteId,
      encounterUid: selectedEncounterUid,
    },
    {
      skip: !selectedEncounterUid,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  )

  const items = useMemo(() => {
    if (!proceduresData?.items || !selectedEncounterUid) return []
    return proceduresData.items?.filter((item) => item.code_type === ProcedureTypeCode.CPT4)
  }, [proceduresData])

  const previousTreatments = useMemo(
    () => toPreviousTreatments(proceduresData?.historical_procedures, selectedEncounterUid),
    [proceduresData?.historical_procedures, selectedEncounterUid],
  )
  const hasProcedures = items.length > 0
  const isGeneralNoteInteractionDisabled = disableActions || !hasProcedures

  const handleOpenManageProcedure = useCallback(
    (procedure?: ProcedureItem) => {
      setSelectedManageProcedure(procedure || null)
      toggleManageProcedure(true)
    },
    [toggleManageProcedure],
  )

  useEffect(() => {
    if (!onRegisterAction) return
    onRegisterAction(
      <div className={styles.actionsRow}>
        <Button
          className={styles.addCptButton}
          proportion={BUTTON_PROPORTION.SMALL}
          onClick={() => handleOpenManageProcedure()}
          severity={BUTTON_SEVERITY.SUCCESS_FILLED}
          disabled={!selectedEncounterUid || disableActions}
          icon={
            <div className={styles.iconWrapper}>
              <Icon icon='ph:plus' width={12} height={12} />
            </div>
          }>
          Add CPT/ICD
        </Button>
      </div>,
    )
    return () => {
      onRegisterAction(null)
    }
  }, [handleOpenManageProcedure, onRegisterAction, selectedEncounterUid, disableActions])

  const handleCloseManageProcedure = () => {
    setSelectedManageProcedure(null)
    toggleManageProcedure(false)
  }

  const handleOpenProductScanner = useCallback(
    (procedure: ProcedureItem) => {
      setProcedureToScan(procedure)
      toggleProductScanner(true)
    },
    [toggleProductScanner],
  )

  const handleCloseProductScanner = () => {
    setProcedureToScan(null)
    toggleProductScanner(false)
  }

  const handleProductsScanned = useCallback(
    async (products: string[]) => {
      if (!procedureToScan || !siteId || !selectedEncounterUid) {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Error',
          description: 'Missing procedure or site information',
        })
        return
      }

      try {
        const updatedProcedureData = {
          id: procedureToScan.id,
          code: procedureToScan.code,
          code_type: procedureToScan.code_type,
          procedure_units: procedureToScan.procedure_units,
          units: procedureToScan.units,
          fee: procedureToScan.fee,
          provider_id: procedureToScan.provider_id,
          justify_codes: procedureToScan.justify_codes,
          product_labels: products,
        }

        const result = await updateProcedure({
          siteId,
          encounterUid: selectedEncounterUid,
          data: {
            procedures: [updatedProcedureData],
          },
        }).unwrap()

        if (result && typeof result === 'object' && 'success' in result) {
          const responseResult = result as {
            success: boolean
            error?: string
            statusCode?: number
          }
          if (!responseResult.success) {
            setNotification({
              type: ALERT_CONSTANTS.ERROR,
              duration: 2,
              title: 'Error',
              description: responseResult.error || 'Failed to save product labels',
            })
            return
          }
        }
      } catch (error: any) {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Error',
          description: error?.data?.message || 'Failed to save product labels',
        })
      }
    },
    [procedureToScan, siteId, selectedEncounterUid, updateProcedure, setNotification],
  )

  const columns = useMemo(() => {
    if (disableActions) {
      return baseColumns()
    }
    return getTreatmentsTableColumns({
      onEditProcedure: handleOpenManageProcedure,
      onScanProducts: handleOpenProductScanner,
      onNavigateToForms,
    })
  }, [handleOpenManageProcedure, handleOpenProductScanner, disableActions, onNavigateToForms])

  const itemIds = useMemo(() => items.map((item) => item.id).join(','), [items])
  const prevEncounterUidRef = useRef<string | null>(selectedEncounterUid)

  useEffect(() => {
    if (prevEncounterUidRef.current !== selectedEncounterUid) {
      prevEncounterUidRef.current = selectedEncounterUid
      handleSetSelectedProcedure(null)
      setNoteDrafts({})
      setIsEditingGeneralNote(false)
      setGeneralNoteDraft('')
      setGeneralNoteAutosaveStatus('idle')
      setGeneralNoteLastSavedAt(null)
      return
    }
  }, [selectedEncounterUid, handleSetSelectedProcedure])

  useEffect(() => {
    setNoteDrafts({})
    setIsEditingGeneralNote(false)
    setGeneralNoteDraft('')
    setGeneralNoteAutosaveStatus('idle')
    setGeneralNoteLastSavedAt(null)
  }, [selectedPatientId])

  useEffect(() => {
    if (isFetching) return

    if (items.length === 0) {
      if (selectedProcedureId) {
        handleSetSelectedProcedure(null)
      }
      return
    }

    const stillExists = selectedProcedureId
      ? items.find((item) => item.id === selectedProcedureId)
      : null

    if (stillExists) return

    handleSetSelectedProcedure(items[0])
  }, [itemIds, selectedProcedureId, items, isFetching, handleSetSelectedProcedure])

  const getRowClassName = useCallback(
    (record: ProcedureItem) => {
      return classNames({
        [styles.selectedRow]: selectedProcedureId === record.id,
      })
    },
    [selectedProcedureId],
  )

  const clearProcedureNoteAutosaveTimer = useCallback(() => {
    if (procedureNoteAutosaveTimeoutRef.current) {
      clearTimeout(procedureNoteAutosaveTimeoutRef.current)
      procedureNoteAutosaveTimeoutRef.current = null
    }
  }, [])

  const clearGeneralNoteAutosaveTimer = useCallback(() => {
    if (generalNoteAutosaveTimeoutRef.current) {
      clearTimeout(generalNoteAutosaveTimeoutRef.current)
      generalNoteAutosaveTimeoutRef.current = null
    }
  }, [])

  const handleGeneralNoteDisabledClick = useCallback(() => {
    if (disableActions || hasProcedures) {
      return
    }
  }, [disableActions, hasProcedures])

  const persistProcedureNoteDrafts = useCallback(async () => {
    if (disableActions) {
      return
    }

    if (isProcedureNoteAutosavingRef.current) {
      rerunProcedureNoteAutosaveRef.current = true
      return
    }

    if (!siteId || !selectedEncounterUid) {
      return
    }

    const draftEntries = Object.entries(noteDrafts)
    if (draftEntries.length === 0) {
      return
    }

    const proceduresById = new Map(items.map((item) => [item.id, item]))
    const pendingUpdates = draftEntries.flatMap(([procedureIdRaw, draftValue]) => {
      const procedureId = Number(procedureIdRaw)
      const procedure = proceduresById.get(procedureId)

      if (!procedure) {
        return []
      }

      const currentValue = procedure.note ?? ''
      if (currentValue === draftValue) {
        return []
      }

      return [
        {
          procedureId,
          draftValue,
          payload: {
            ...toProcedurePayloadBase(procedure),
            note: draftValue,
          },
        },
      ]
    })

    if (pendingUpdates.length === 0) {
      setNoteDrafts((prevDrafts) => {
        const nextDrafts = { ...prevDrafts }

        Object.entries(prevDrafts).forEach(([procedureIdRaw, draftValue]) => {
          const procedureId = Number(procedureIdRaw)
          const procedure = proceduresById.get(procedureId)
          const currentValue = procedure?.note ?? ''

          if (!procedure || currentValue === draftValue) {
            delete nextDrafts[procedureId]
          }
        })

        return nextDrafts
      })
      return
    }

    isProcedureNoteAutosavingRef.current = true

    try {
      const saveSnapshot = pendingUpdates.reduce<Record<number, string>>((acc, update) => {
        acc[update.procedureId] = update.draftValue
        return acc
      }, {})

      await autosaveUpdateProcedure({
        siteId,
        encounterUid: selectedEncounterUid,
        skipESignInvalidation: true,
        data: {
          procedures: pendingUpdates.map((update) => update.payload),
        },
      }).unwrap()

      setNoteDrafts((prevDrafts) => {
        const nextDrafts = { ...prevDrafts }

        Object.entries(saveSnapshot).forEach(([procedureIdRaw, draftValue]) => {
          const procedureId = Number(procedureIdRaw)

          if (nextDrafts[procedureId] === draftValue) {
            delete nextDrafts[procedureId]
          }
        })

        return nextDrafts
      })
    } catch (error: any) {
      setNotification({
        type: ALERT_CONSTANTS.ERROR,
        duration: 2,
        title: 'Autosave failed',
        description: error?.data?.message || 'Unable to autosave procedure note.',
      })
    } finally {
      isProcedureNoteAutosavingRef.current = false

      if (rerunProcedureNoteAutosaveRef.current) {
        rerunProcedureNoteAutosaveRef.current = false
        void persistProcedureNoteDrafts()
      }
    }
  }, [
    autosaveUpdateProcedure,
    disableActions,
    items,
    noteDrafts,
    selectedEncounterUid,
    setNotification,
    siteId,
  ])

  const persistGeneralNoteDraft = useCallback(async () => {
    if (isGeneralNoteInteractionDisabled || !isEditingGeneralNote) {
      return
    }

    if (isGeneralNoteAutosavingRef.current) {
      rerunGeneralNoteAutosaveRef.current = true
      return
    }

    if (!siteId || !selectedEncounterUid) {
      return
    }

    const draftSnapshot = generalNoteDraft.trim()
    if (draftSnapshot.length === 0) {
      setGeneralNoteAutosaveStatus('idle')
      return
    }

    const serverGeneralNote = proceduresData?.general_note || ''
    if (draftSnapshot === serverGeneralNote) {
      return
    }

    isGeneralNoteAutosavingRef.current = true
    setGeneralNoteAutosaveStatus('saving')

    try {
      const proceduresPayload = items.map((item) => toProcedurePayloadBase(item))

      const result = await autosaveUpdateProcedure({
        siteId,
        encounterUid: selectedEncounterUid,
        skipESignInvalidation: true,
        data: {
          procedures: proceduresPayload,
          general_note: draftSnapshot,
        },
      }).unwrap()

      const mutationFailureMessage = getMutationFailureMessage(
        result,
        'Unable to autosave general note.',
      )
      if (mutationFailureMessage) {
        throw { data: { message: mutationFailureMessage } }
      }

      setGeneralNoteAutosaveStatus('saved')
      setGeneralNoteLastSavedAt(
        DateService.formatDisplayDate(new Date().toISOString(), true, false, false).trim(),
      )
    } catch (error: any) {
      setGeneralNoteAutosaveStatus('error')
      setNotification({
        type: ALERT_CONSTANTS.ERROR,
        duration: 2,
        title: 'Autosave failed',
        description: error?.data?.message || 'Unable to autosave general note.',
      })
    } finally {
      isGeneralNoteAutosavingRef.current = false

      if (rerunGeneralNoteAutosaveRef.current) {
        rerunGeneralNoteAutosaveRef.current = false
        void persistGeneralNoteDraft()
      }
    }
  }, [
    autosaveUpdateProcedure,
    generalNoteDraft,
    isGeneralNoteInteractionDisabled,
    isEditingGeneralNote,
    items,
    proceduresData?.general_note,
    selectedEncounterUid,
    setNotification,
    siteId,
  ])

  const flushAutosaveNow = useCallback(async () => {
    clearProcedureNoteAutosaveTimer()
    clearGeneralNoteAutosaveTimer()
    await persistProcedureNoteDrafts()
    await persistGeneralNoteDraft()
  }, [
    clearGeneralNoteAutosaveTimer,
    clearProcedureNoteAutosaveTimer,
    persistGeneralNoteDraft,
    persistProcedureNoteDrafts,
  ])

  const handleNoteChange = useCallback((procedureId: number, value: string) => {
    setNoteDrafts((prev) => ({
      ...prev,
      [procedureId]: value,
    }))
    if (activeTranscriptionRef.current?.procedureId === procedureId) {
      activeTranscriptionRef.current = null
    }
  }, [])

  const handleEditGeneralNote = useCallback(() => {
    if (isGeneralNoteInteractionDisabled) {
      return
    }

    if (isEditingGeneralNote) {
      clearGeneralNoteAutosaveTimer()
      void persistGeneralNoteDraft()
      setIsEditingGeneralNote(false)
      return
    }

    setGeneralNoteDraft(proceduresData?.general_note || '')
    setIsEditingGeneralNote(true)
    setGeneralNoteAutosaveStatus('idle')
    setGeneralNoteLastSavedAt(null)
  }, [
    clearGeneralNoteAutosaveTimer,
    disableActions,
    isGeneralNoteInteractionDisabled,
    isEditingGeneralNote,
    persistGeneralNoteDraft,
    proceduresData?.general_note,
    setNotification,
  ])

  const handleSaveNote = useCallback(
    async (procedure: ProcedureItem) => {
      const noteValue = noteDrafts[procedure.id] ?? procedure.note ?? ''

      if (!siteId || !selectedEncounterUid) {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Error',
          description: 'Missing site or encounter information',
        })
        return
      }

      try {
        const updatedProcedureData = {
          ...toProcedurePayloadBase(procedure),
          note: noteValue,
        }

        const result = await updateProcedure({
          siteId,
          encounterUid: selectedEncounterUid,
          data: {
            procedures: [updatedProcedureData],
          },
        }).unwrap()

        if (result && typeof result === 'object' && 'success' in result) {
          const responseResult = result as {
            success: boolean
            error?: string
            statusCode?: number
          }
          if (!responseResult.success) {
            setNotification({
              type: ALERT_CONSTANTS.ERROR,
              duration: 2,
              title: 'Error',
              description: responseResult.error || 'Failed to save note',
            })
            return
          }
        }

        setNoteDrafts((prev) => {
          const next = { ...prev }
          delete next[procedure.id]
          return next
        })
      } catch (error: any) {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Error',
          description: error?.data?.message || 'Failed to save note',
        })
      }
    },
    [noteDrafts, siteId, selectedEncounterUid, updateProcedure, setNotification],
  )

  const handleSaveGeneralNote = useCallback(async () => {
    if (!siteId || !selectedEncounterUid) {
      setNotification({
        type: ALERT_CONSTANTS.ERROR,
        duration: 2,
        title: 'Error',
        description: 'Missing site or encounter information',
      })
      return
    }

    if (!hasProcedures) {
      return
    }

    const draftSnapshot = generalNoteDraft.trim()
    if (draftSnapshot.length === 0) {
      return
    }

    try {
      const proceduresData = items.map((item) => toProcedurePayloadBase(item))

      const result = await updateProcedure({
        siteId,
        encounterUid: selectedEncounterUid,
        data: {
          procedures: proceduresData,
          general_note: draftSnapshot,
        },
      }).unwrap()

      const mutationFailureMessage = getMutationFailureMessage(
        result,
        'Failed to save general note',
      )
      if (mutationFailureMessage) {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Error',
          description: mutationFailureMessage,
        })
        return
      }

      setGeneralNoteDraft('')
      setIsEditingGeneralNote(false)
    } catch (error: any) {
      setNotification({
        type: ALERT_CONSTANTS.ERROR,
        duration: 2,
        title: 'Error',
        description: error?.data?.message || 'Failed to save general note',
      })
    }
  }, [
    generalNoteDraft,
    hasProcedures,
    siteId,
    selectedEncounterUid,
    updateProcedure,
    setNotification,
    items,
  ])

  useEffect(() => {
    clearProcedureNoteAutosaveTimer()

    if (
      disableActions ||
      !siteId ||
      !selectedEncounterUid ||
      Object.keys(noteDrafts).length === 0
    ) {
      return
    }

    procedureNoteAutosaveTimeoutRef.current = setTimeout(() => {
      void persistProcedureNoteDrafts()
    }, NOTES_AUTOSAVE_DELAY_MS)

    return clearProcedureNoteAutosaveTimer
  }, [
    clearProcedureNoteAutosaveTimer,
    disableActions,
    noteDrafts,
    persistProcedureNoteDrafts,
    selectedEncounterUid,
    siteId,
  ])

  useEffect(() => {
    clearGeneralNoteAutosaveTimer()

    if (
      isGeneralNoteInteractionDisabled ||
      !isEditingGeneralNote ||
      !siteId ||
      !selectedEncounterUid
    ) {
      return
    }

    if (generalNoteDraft.trim().length === 0) {
      setGeneralNoteAutosaveStatus('idle')
      return
    }

    const serverGeneralNote = proceduresData?.general_note || ''
    if (generalNoteDraft === serverGeneralNote) {
      return
    }

    generalNoteAutosaveTimeoutRef.current = setTimeout(() => {
      void persistGeneralNoteDraft()
    }, NOTES_AUTOSAVE_DELAY_MS)

    return clearGeneralNoteAutosaveTimer
  }, [
    clearGeneralNoteAutosaveTimer,
    generalNoteDraft,
    isGeneralNoteInteractionDisabled,
    isEditingGeneralNote,
    persistGeneralNoteDraft,
    proceduresData?.general_note,
    selectedEncounterUid,
    siteId,
  ])

  useEffect(() => {
    if (!hasProcedures && isEditingGeneralNote) {
      clearGeneralNoteAutosaveTimer()
      setIsEditingGeneralNote(false)
      setGeneralNoteAutosaveStatus('idle')
      setGeneralNoteLastSavedAt(null)
    }
  }, [clearGeneralNoteAutosaveTimer, hasProcedures, isEditingGeneralNote])

  useEffect(() => {
    if (!onRegisterLeaveAction) {
      return
    }

    onRegisterLeaveAction(flushAutosaveNow)

    return () => {
      onRegisterLeaveAction(null)
    }
  }, [flushAutosaveNow, onRegisterLeaveAction])

  useEffect(() => {
    return () => {
      clearProcedureNoteAutosaveTimer()
      clearGeneralNoteAutosaveTimer()
    }
  }, [clearGeneralNoteAutosaveTimer, clearProcedureNoteAutosaveTimer])

  const expandedRowRender = useCallback(
    (record: ProcedureItem) => {
      const noteValue = noteDrafts[record.id] ?? record.note ?? ''

      return (
        <div className={styles.expandedRow}>
          <div className={styles.noteFormGroup}>
            <TextArea
              autoSize={{ minRows: 3 }}
              value={noteValue}
              onChange={(e) => handleNoteChange(record.id, e.target.value)}
              placeholder='Type here...This note will NOT SHOW in medical chart.'
              disabled={disableActions}
            />
          </div>
          <div className={styles.noteActions}>
            <Button
              proportion={BUTTON_PROPORTION.SMALL}
              severity={BUTTON_SEVERITY.SUCCESS_FILLED}
              onClick={() => handleSaveNote(record)}
              loading={isUpdatingProcedure}
              disabled={disableActions}>
              Save
            </Button>
          </div>
        </div>
      )
    },
    [noteDrafts, handleNoteChange, handleSaveNote, disableActions, isUpdatingProcedure],
  )

  const generalNoteValue = isEditingGeneralNote
    ? generalNoteDraft
    : proceduresData?.general_note || ''
  const generalNoteAutosaveLabel =
    generalNoteAutosaveStatus === 'saving'
      ? 'Saving...'
      : generalNoteAutosaveStatus === 'saved' && generalNoteLastSavedAt
      ? `Last autosaved at ${generalNoteLastSavedAt}`
      : generalNoteAutosaveStatus === 'error'
      ? 'Autosave failed'
      : 'Input is autosaving'

  return (
    <>
      <>
        <ManageProcedure
          isVisible={isVisibleManageProcedure}
          onClose={handleCloseManageProcedure}
          procedure={selectedManageProcedure}
        />
        {isVisibleProductScanner && (
          <ProductScanner
            isOpen={isVisibleProductScanner}
            onClose={handleCloseProductScanner}
            onProductsScanned={handleProductsScanned}
            defaultProducts={procedureToScan?.product_labels}
          />
        )}
      </>
      <Spin spinning={isFetching || isUpdatingProcedure || !selectedPatientId}>
        <TreatmentsDataSection
          items={items}
          columns={columns}
          getRowClassName={getRowClassName}
          expandedRowRender={expandedRowRender}
          isFetching={isFetching}
        />
        <GeneralNote
          value={generalNoteValue}
          isEditing={isEditingGeneralNote}
          disableActions={isGeneralNoteInteractionDisabled}
          onDisabledClick={
            !disableActions && !hasProcedures ? handleGeneralNoteDisabledClick : undefined
          }
          isSaving={isUpdatingProcedure}
          autosaveLabel={generalNoteAutosaveLabel}
          autosaveStatus={generalNoteAutosaveStatus}
          onStartEdit={handleEditGeneralNote}
          onChange={(value) => {
            setGeneralNoteDraft(value)
            setGeneralNoteAutosaveStatus('idle')
            setGeneralNoteLastSavedAt(null)
          }}
          onCancel={() => {
            setIsEditingGeneralNote(false)
            setGeneralNoteDraft('')
            setGeneralNoteAutosaveStatus('idle')
            setGeneralNoteLastSavedAt(null)
          }}
          onSave={handleSaveGeneralNote}
        />
        <div style={{ marginTop: 24, marginBottom: 16 }}>
          <PreviousTreatments treatments={previousTreatments} />
        </div>
        <TreatmentsHistoricalSection
          isFetching={isFetching}
          selectedPatientId={selectedPatientId}
          selectedEncounterUid={selectedEncounterUid}
          historicalData={proceduresData?.historical_procedures}
        />
      </Spin>
    </>
  )
}
